<template>
    <!-- this is the Vue component for creating a new post -->
    <validation-observer tag="div" ref="new_post">
        <div ref="newPostContainer" v-if="user && (filteredBoards.length > 0 || board_id_prop !== false)" class="new-post card card-shadow"
             :class="[{'closed': !container_open}, {'open': container_open}, {'idle': postTitle !== '' && container_open === false}]"
             v-click-outside="vcoConfig">
            <!-- new post top -->
            <div class="card-top new-post-top no-select tw-rounded-t">
                <div class="card-inner">
                    <div class="post-option" v-for="post_type in postTypes"
                         @click="openContainer(); setPostType(post_type.name_clean);"
                         :class="{'active-post-option': postType === post_type.name_clean}"
                         v-if="hasPlan(post_type.plan_number) && (post_type.name_clean !== 'poll' || (!isHelpdesk && !is_helpdesk_prop))">
                        <i class="far" :class="'fa-' + post_type.icon"></i>
                        <h3>{{ post_type.name }}</h3>
                    </div>
                </div>
            </div>
            <!-- new post content -->
            <div class="new-post-content tw-rounded-b">
                <div class="card-inner">
                    <template v-if="postType === 'poll'">
                        <!-- poll question -->
                        <huddle-editor-v2   class="new-post-title"
                                            v-model="pollQuestion"
                                            :single_line="true"
                                            :allow_enter_shift="false"
                                            :placeholder="translations['forum.poll_question_placeholder']"
                                            @focus="openContainer"
                                            rules="required|min:5|max:100"
                                            name="question"
                                            key="question">
                        </huddle-editor-v2>
                    </template>
                    <template v-else>
                        <!-- topic title for everything except poll -->
                        <huddle-editor-v2   class="new-post-title"
                                            v-model="postTitle"
                                            :single_line="true"
                                            :allow_enter_shift="false"
                                            :placeholder="translations['forum.title_placeholder']"
                                            @focus="openContainer"
                                            rules="required|max:100"
                                            name="title"
                                            key="title">
                        </huddle-editor-v2>
                    </template>
                    <div v-show="container_open" class="sequential-fields">
                        <template v-if="postType === 'poll'">
                            <!-- Image uploader -->
                            <div class="switch-wrapper tw-mt-4">
                                <div class="switch"
                                        :class="{'active': show_poll_image_upload}"
                                        @click="toggleImageUpload">
                                    <div class="thumb"></div>
                                </div>
                                <p class="flex-1">{{ $t('poll.add_image') }}</p>
                            </div>
                            <validation-provider v-if="show_poll_image_upload" name="image" tag="div" class="tw-mt-4" v-slot="{validate, errors}">
                                <input ref="newPollImagePicker" class="hidden" type="file"
                                        accept=".jpg, .png, .jpeg, .gif, .bmp, .heic, .heif" @change="addImage($event); validate($event);">
                                <div class="filedrop-wrapper filedrop-trigger" v-if="pollImages.length === 0"
                                        @click="openPollImageUploader"></div>
                                <div class="image-previews">
                                    <new-post-images v-for="(preview, index) in pollImagesPreview" :preview="preview"
                                                        :index="index" :key="pollImagesUUIDs[index]" :images="pollImages"
                                                        :uuids="pollImagesUUIDs"
                                                        @imageUploaded="removeImageFromUploadingList"
                                                        @imageRemoved="removeImage"></new-post-images>
                                    <div class="image-preview" v-if="show_upload_preview_box">
                                        <img class="tw-invisible">
                                    </div>
                                    <div class="filedrop-trigger-inner" v-if="pollImages.length > 0 && pollImagesCount < 4"
                                             @click="openPollImageUploader"></div>
                                </div>
                                <span class="form-validation-error tw-mb-3" v-if="errors.length > 0">{{ errors[0] }}.</span>
                            </validation-provider>
                            <!-- poll answers -->
                            <div class="poll-settings tw-rounded tw-bg-gray-0 tw-p-4 tw-mt-4">
                                <p class="tw-text-gray-600 tw-font-semibold tw-mb-4">{{ translations['forum.poll_answers'] }}</p>
                                <template v-for="(answer, index) in poll_answers">
                                    <div class="tw-flex tw-items-center" :key="poll_answers.uid">
                                        <huddle-editor-v2   class="tw-flex-1"
                                                            v-model="answer.value"
                                                            :board_id_prop="board_id_prop"
                                                            :single_line="true"
                                                            :allow_enter_shift="false"
                                                            :placeholder="translations['poll.answer']"
                                                            rules="required"
                                                            :name="'pollanswer' + index"
                                                            :key="'pollanswer' + index">
                                        </huddle-editor-v2>
                                        <div class="tw-flex tw-items-center tw-justify-center tw-px-6 tw--mt-4"
                                             v-if="pollAnswersLength === index+1 || pollAnswersLength > 2">
                                            <i class="fa fa-trash warning-color hover-opacity"
                                               v-if="pollAnswersLength > 2" @click="deleteAnswer(answer, index)"></i>
                                            <i class="fa fa-plus tw-text-gray-600 hover-opacity"
                                               :class="{'tw-pl-6': pollAnswersLength > 2}"
                                               v-if="pollAnswersLength === index+1" @click="addPollAnswer"></i>
                                        </div>
                                    </div>
                                </template>
                                <!-- poll settings -->
                                <p class="tw-text-gray-600 tw-font-semibold tw-mb-4">{{ translations['poll.settings'] }}</p>
                                <div class="grid col-2 col-gap-2">
                                    <div class="switch-wrapper">
                                        <div class="switch" :class="{'active': poll_settings.retry === '1'}"
                                             @click="switchPollSetting('retry')">
                                            <div class="thumb"></div>
                                        </div>
                                        <p class="flex-1">{{ translations['poll.allow_changing_answer'] }}</p>
                                    </div>
                                    <div class="switch-wrapper">
                                        <div class="switch"
                                             :class="{'active': poll_settings.poll_allow_guest_answers === '1'}"
                                             @click="switchPollSetting('poll_allow_guest_answers')">
                                            <div class="thumb"></div>
                                        </div>
                                        <p class="flex-1">{{ translations['poll.allow_guest_answers'] }}</p>
                                    </div>
                                </div>
                                <div class="grid col-2 col-gap-2 tw-mt-3">
                                    <div class="switch-wrapper">
                                        <div class="switch" :class="{'active': poll_settings.poll_public === '1'}"
                                             @click="switchPollSetting('poll_public')">
                                            <div class="thumb"></div>
                                        </div>
                                        <p class="flex-1">{{ translations['poll.show_who_voted_what'] }}</p>
                                    </div>
                                    <div class="switch-wrapper">
                                        <div class="switch"
                                             :class="{'active': poll_settings.poll_multiple_answers === '1'}"
                                             @click="switchPollSetting('poll_multiple_answers')">
                                            <div class="thumb"></div>
                                        </div>
                                        <p class="flex-1">{{ translations['poll.allow_multiple_answers'] }}</p>
                                    </div>
                                </div>
                                <div class="tw-flex tw-items-center tw-text-gray-400 hover-opacity tw-mt-4"
                                     @click="togglePollScheduler" v-if="poll_scheduled_date === ''">
                                    <i class="far fa-calendar-alt tw-pr-2"></i>
                                    <p>{{ translations['poll.add_end_date'] }}</p>
                                </div>
                                <div class="poll-scheduled-date" @click="preventContainerFromClosing"
                                     v-if="poll_scheduler_opened || poll_scheduled_date !== ''"
                                     :class="{'show': poll_scheduled_date}">
                                    <datetime ref="pollDatePicker"
                                              v-on:input="setPollDate"
                                              :format="{ year: 'numeric', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short' }"
                                              v-on:close="togglePollScheduler"
                                              type="datetime"
                                              v-model="poll_scheduled_date"
                                              value-zone="Europe/Amsterdam"
                                              auto></datetime>
                                </div>
                            </div>
                        </template>
                        <template v-else>
                            <huddle-editor-v2   type="textarea"
                                                :board_id_prop="board_id_prop"
                                                v-model="postContent"
                                                :emojis="true"
                                                :mentions="true"
                                                rules="required|min:5"
                                                name="content"
                                                :placeholder="translations['forum.whats_up']">
                            </huddle-editor-v2>
                            <huddle-editor-v2   v-if="postType === 'video'"
                                                v-model="videoUrl"
                                                :single_line="true"
                                                :allow_enter_shift="false"
                                                placeholder="Youtube / Vimeo video URL"
                                                :rules="{ required: true, regex: /^(http(s)??\:\/\/)?(www\.)?((youtube\.com\/watch\?v=)|(youtu.be\/))([a-zA-Z0-9\-_])+/ && /^.*(vimeo.com\/|v\/|u\/\w\/|\/|\|\))([^#\&\?]*).*/ }"
                                                name="video">
                            </huddle-editor-v2>
                            <div class="video-preview-wrapper" v-if="videoUrl !== ''">
                                <div class="responsive-video embed-16-by-9" v-if="postType === 'video' && videoPreview">
                                    <iframe :src="videoPreview" class="topic-video" frameborder="0"
                                            allow="autoplay; encrypted-media" allowfullscreen></iframe>
                                </div>
                            </div>
                            <div class="no-select" v-if="postType === 'image'">
                                <validation-provider rules="required" name="image" v-slot="{validate, errors}">
                                    <input ref="newPostImagePicker" class="hidden" type="file"
                                           accept=".jpg, .png, .jpeg, .gif, .bmp, .heic, .heif" @change="addImage($event); validate($event);">
                                    <div class="filedrop-wrapper filedrop-trigger" v-if="postImages.length === 0"
                                         @click="openImageUploader"></div>
                                    <div class="image-previews">
                                        <new-post-images v-for="(preview, index) in postImagesPreview" :preview="preview"
                                                         :index="index" :key="postImagesUUIDs[index]" :images="postImages"
                                                         :uuids="postImagesUUIDs"
                                                         @imageUploaded="removeImageFromUploadingList"
                                                         @imageRemoved="removeImage"></new-post-images>
                                        <div class="image-preview" v-if="show_upload_preview_box">
                                            <img class="tw-invisible">
                                        </div>
                                        <div class="filedrop-trigger-inner" v-if="postImages.length > 0 && postImagesCount < 4"
                                             @click="openImageUploader"></div>
                                    </div>

                                    <span class="form-validation-error tw-mb-3" v-if="errors.length > 0">{{ errors[0] }}.</span>
                                </validation-provider>
                            </div>
                        </template>
                    </div>
                </div>
                <!-- new post bottom -->
                <div v-show="container_open" class="new-post-bottom no-select">
                    <div class="card-inner">
                        <div class="flex flex-align-end">
                            <div class="flex-6 flex-no-padding" style="margin-bottom: 5px;">
                                <div class="tw-flex tw-items-center tw-text-sm tw-p-1 extra-light-text" v-if="!boardsVisible && (isHelpdesk || is_helpdesk_prop)">
                                    <i class="fal fa-eye-slash tw-pr-2"></i>
                                    <span>Helpdesk tickets are private between you and administrators.</span>
                                </div>
                                <!-- all boards -->
                                <div class="boards" v-if="boardsVisible">
                                    <p class="light-text">{{ translations['social.relevant_board'] }}?</p>

                                    <div class="new-board-select hover-opacity" v-for="board in filteredBoards"
                                         :style="{ background: board.color }"
                                         :class="{'is-active': board.id === activeBoard.id}"
                                         @click="setBoard(board, 'main')">
                                        {{ board.name }}
                                    </div>
                                </div>
                                <!-- all subboards -->
                                <template v-if="activeBoard !== '' && boardsVisible">
                                    <div v-if="subboards.length > 0" class="subboards">
                                        <p class="light-text">{{ translations['social.relevant_sub_board'] }}</p>
                                        <div v-for="subboard in subboards"
                                             v-if="subboard.permissions.includes('WRITE')"
                                             class="new-board-select hover-opacity"
                                             :class="{'is-active': subboard.id === activeSubboard.id}"
                                             :style="{ background: activeBoard.color }"
                                             @click="setBoard(subboard, 'sub')">
                                            {{ subboard.name }}
                                        </div>
                                    </div>
                                </template>
                                <span class="form-validation-error" v-if="no_board_error">A board must be selected.</span>
                                <div class="media-actions flex relative">
                                    <div class="media-action flex flex-center-vh relative"
                                         :class="{'secondary-c active': postSticky}" @click="toggleSticky"
                                         v-if="user.isAdmin">
                                        <i class="fa fa-thumbtack"></i>
                                    </div>
                                    <div class="media-action flex flex-center-vh relative"
                                         :class="{'secondary-c active': postAttachments.length > 0}"
                                         @click="chooseAttachments" v-if="postType === 'text'">
                                        <input ref="newPostAttachments" class="hidden" type="file"
                                               @change="uploadAttachments" multiple>
                                        <i class="fa fa-paperclip"></i>
                                        <span class="flex flex-center-vh absolute secondary-c-bg secondary-c-contrast"
                                              v-if="postAttachments.length > 0">{{ postAttachments.length }}</span>
                                    </div>
                                    <div class="media-action flex flex-center-vh relative"
                                         :class="{'secondary-c active': schedulerOpened || postDate}"
                                         @click="toggleScheduler" v-if="user.isAdmin">
                                        <i class="fa fa-clock"></i>
                                    </div>
                                    <div v-if="schedulerOpened" class="scheduler-wrapper absolute"
                                         :class="{'show': postDate}">
                                        <datetime ref="datePicker"
                                                  v-on:input="setDate"
                                                  :format="{ year: 'numeric', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short' }"
                                                  v-on:close="toggleScheduler"
                                                  type="datetime"
                                                  v-model="postDate"
                                                  value-zone="Europe/Amsterdam"
                                                  auto></datetime>
                                    </div>
                                </div>
                            </div>
                            <div class="flex-2 flex-no-padding">
                                <button @click="postTopic"
                                        class="post-button hover-opacity secondary-c-contrast secondary-c-bg"
                                        :class="{'tw-opacity-50': submitDisabled}">
                                    {{ translations['forum.submit_new_topic'] }}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </validation-observer>
</template>

<script>
    import { mapState } from 'vuex';
    import smoothReflow from 'vue-smooth-reflow';
    import vClickOutside from 'v-click-outside'
    import { Datetime } from 'vue-datetime';
    import loadImage from 'blueimp-load-image';
    import filter from 'lodash.filter';
    import moment from 'moment';

    import Global from '../../mixins/Global.js'
    import PreventFromClosing from '../../mixins/PreventFromClosing.js'
    import VideoRegexMixin from '../../mixins/v2/VideoRegexMixin.js'
    import Plans from '../../mixins/v2/Plans.js';
    import heic2any from "heic2any";
    import Channel from "@/resources/assets/js/vue/lib/models/Channel";

    export default {
        mixins: [smoothReflow, Global, PreventFromClosing, VideoRegexMixin, Plans],
        name: 'NewPost',

        components: {
            datetime: Datetime,
        },

        props: {
            allow_polls: {
                default: false,
            },
            is_helpdesk_prop: {
                default: false,
            },
            board_id_prop: {
                default: false,
            },
        },

        data() {
            return {
                boardsVisible: true,
                schedulerOpened: false,
                poll_answers: [
                    {
                        uid: 0,
                        value: '',
                    },
                    {
                        uid: 1,
                        value: '',
                    }
                ],
                poll_settings: {
                    retry: '0',
                    poll_allow_guest_answers: '0',
                    poll_public: '0',
                    poll_multiple_answers: '0',
                },
                poll_scheduler_opened: false,
                poll_scheduled_date: '',
                show_poll_image_upload: false,
                currently_uploading_images: [],
                submit_disabled: false,
                show_upload_preview_box: false,
                currently_uploading_attachments: [],
                isHelpdesk: isHelpdesk,
                vcoConfig: {
                    handler: this.closeContainer,
                    events: ['mousedown'],
                },
                no_board_error: false,
                container_open: false,
            }
        },

        computed: {
            ...mapState([
                'activeBoard',
                'activeSubboard',
                'activeMainBoardId',
                'postType',
                'initImagePicker',
                'postImagesCount',
                'postImages',
                'postImagesPreview',
                'postSticky',
                'postAttachments',
                'postImagesUUIDs',
                'postAttachmentsUUIDs',
                'pollImages',
                'pollImagesPreview',
                'pollImagesUUIDs',
                'pollImagesCount',
            ]),

            boards() {
                return Channel.query()
                    .where('template', 'default')
                    .where('homepage', true)
                    .where('subgroup_id', null)
                    .where('source', 'system')
                    .where((channel) => channel.permissions.includes('WRITE'))
                    .orderBy('index').get();
            },

            subboards() {
                return Channel.query()
                    .where('subgroup_id', this.activeBoard.id)
                    .where('template', 'default')
                    .where('homepage', true)
                    .where('source', 'system')
                    .where((channel) => channel.permissions.includes('WRITE'))
                    .orderBy('index')
                    .get();
            },

            postDate: {
                get() {
                    return this.$store.state.postDate;
                },

                set() {

                }
            },

            postTypes() {
                return [
                    {
                        name_clean: 'text',
                        name: this.translations['social.new_post'],
                        icon: 'pencil',
                        plan_number: 3,
                    },
                    {
                        name_clean: 'image',
                        name: this.translations['social.image'],
                        icon: 'image',
                        plan_number: 3,
                    },
                    {
                        name_clean: 'video',
                        name: 'Video',
                        icon: 'video',
                        plan_number: 3,
                    },
                    {
                        name_clean: 'poll',
                        name: 'Poll',
                        icon: 'poll-h',
                        plan_number: 5,
                    },
                ]
            },

            filteredBoards() {
                return filter(this.boards, function (board) {
                    return board.type !== 'moodboard' && board.homepage === true && board.permissions.includes('WRITE');
                });
            },

            pollAnswersLength() {
                return this.poll_answers.length;
            },

            postTitle: {
                get() {
                    return this.$store.state.postTitle;
                },

                set(val) {
                    this.$store.commit('updateNewPostTitle', val);
                }
            },

            postContent: {
                get() {
                    return this.$store.state.postContent;
                },

                set(val) {
                    this.$store.commit('updateNewPostContent', val);
                }
            },

            pollQuestion: {
                get() {
                    return this.$store.state.pollQuestion;
                },

                set(val) {
                    this.$store.commit('updateNewPollQuestion', val);
                }
            },

            videoUrl: {
                get() {
                    return this.$store.state.videoUrl;
                },

                set(val) {
                    this.$store.commit('updateNewPostVideoUrl', val);
                }
            },

            //if posting a video, regex the link
            videoPreview() {
                if (this.videoUrl !== '' && this.postType === 'video') {
                    if(this.videoUrl.includes('id="isPasted"') || this.videoUrl.includes("id='isPasted'")) { // Froala adds html tags when pasting
                        const video_url_stripped_from_html_tags = this.videoUrl.replace(/<\/?[^>]+(>|$)/g, "");
                        return this.parsedVideo(video_url_stripped_from_html_tags);
                    } else {
                        return this.parsedVideo(this.videoUrl);
                    }
                }

                return false;
            },

            submitDisabled() {
                return this.currently_uploading_images.length > 0 || this.currently_uploading_attachments.length > 0;
            }
        },

        methods: {
            preventContainerFromClosing() {
                this.prevent_from_closing = true;
            },

            openContainer() {
                this.setContainerStatus(true);
            },

            closeContainer(event) {
                // timeout of 0 because we have to wait for HuddleEditor to emit the click event which opens the editor
                setTimeout(() => {
                    if (this.container_open === true && !this.prevent_from_closing && !event.target.classList.contains('fa') && !event.target.classList.contains('new-post-image-preview') && event.target.classList[0] !== 'js') {
                        this.setContainerStatus(false);
                    }
                }, 0);
            },

            setContainerStatus(bool) {
                this.container_open = bool;
            },

            setBoard(board, type) {
                //commit change to the store
                this.$store.commit('setActiveBoard', [board, type]);
                this.no_board_error = false;
            },

            setPostType(type) {
                this.$store.commit('setPostType', type);
            },

            toggleSticky() {
                this.$store.commit('newPostSticky');
            },

            chooseAttachments() {
                this.$refs.newPostAttachments.click();
            },

            uploadAttachments(e) {
                let files = e.target.files;
                let uuids = [];

                for (let i = 0; i < files.length; i++) {
                    let uuid = this.generateUUID();
                    //generate the uuid that we will use to link the topic to this upload
                    uuids.push(uuid);
                    this.currently_uploading_attachments.push(uuid);

                    //this is the image data we will send to the uploads endpoint
                    let file_data = {
                        file: files[i],
                        name: files[i].name,
                        uuid: uuid,
                    };

                    //store the image
                    this.$store.dispatch('storeImage', file_data).then(() => {
                        this.currently_uploading_attachments.splice(this.currently_uploading_attachments.indexOf(uuid), 1);
                    });
                }

                this.$store.commit('updateNewPostAttachments', {
                    files: files,
                    uuids: uuids,
                });
            },

            toggleScheduler() {
                //toggle the scheduler
                this.schedulerOpened = !this.schedulerOpened;
                this.prevent_from_closing = true;

                if (!this.postDate) {
                    //wait for scheduler to be rednered
                    this.$nextTick(() => {
                        //if its visible on the page
                        if (this.$refs.datePicker) {
                            //activate it
                            this.$refs.datePicker.$el.firstElementChild.click();
                        } else {
                            //if its not visible that means we are closing it
                            setTimeout(() => {
                                this.prevent_from_closing = false;
                            }, 100);
                        }
                    });
                }
            },

            setDate(date) {
                if (date) {
                    this.$store.commit('newPostDate', date);

                    setTimeout(() => {
                        this.prevent_from_closing = false;
                    }, 100);
                }
            },

            postTopic() {
                if (!this.submitDisabled) {
                    this.$refs.new_post.validate().then((success) => {
                        if (success) {
                            if (this.activeBoard === '') {
                                this.no_board_error = true;
                            } else {
                                this.no_board_error = false;

                                if (this.postType === 'poll') {
                                    this.createPoll();
                                    this.$refs.new_post.reset();
                                } else {
                                    this.createTopic();
                                    this.$refs.new_post.reset();
                                }
                            }
                        } else {
                            if (this.activeBoard === '') {
                                this.no_board_error = true;
                            }
                        }
                    });
                }
            },

            createTopic(poll_id = false) {
                //activate spinner
                this.$store.commit('toggleSpinner', true);

                //set board
                let channel = 0;

                if (this.activeBoard.id === this.activeSubboard.id || this.activeSubboard === '') {
                    channel = this.activeBoard;
                } else {
                    channel = this.activeSubboard;
                }

                //prepare data
                let data = {
                    'title': this.postTitle,
                    'content': this.postContent,
                    'channel_id': channel.id,
                    'post_type': this.postType,
                    'video_url': this.videoPreview,
                    'upload_uuids': [],
                };

                //polls dont have a title and content, as their question is stored in the quizzes table instead of conversation
                if (this.postType === 'poll') {
                    data['title'] = this.pollQuestion;
                    data['content'] = this.pollQuestion;
                    //we send the quiz_id for the pivot table (quiz_conversation)
                    data['quiz_id'] = poll_id;
                }

                if (this.postSticky) {
                    data['sticky'] = this.postSticky;
                }

                if (this.postDate) {
                    data['release_date'] = moment(this.postDate).format('DD-MM-YYYY HH:mm');
                }

                let postData = {
                    data: data,
                    channel: channel,
                };

                //image data we use to display the image in the topic after posting
                if (this.postType === 'image') {
                    postData['images'] = this.postImages;
                    postData['imagesPreviews'] = this.postImagesPreview;
                    data['upload_uuids'] = data['upload_uuids'].concat(this.postImagesUUIDs);
                }

                if (this.postType === 'poll') {
                    postData['images'] = this.pollImages;
                    postData['imagesPreviews'] = this.pollImagesPreview;
                    data['upload_uuids'] = data['upload_uuids'].concat(this.pollImagesUUIDs);
                }

                if (this.postType === 'text') {
                    data['upload_uuids'] = data['upload_uuids'].concat(this.postAttachmentsUUIDs);
                    postData['attachments'] = this.postAttachments;
                }

                this.$store.dispatch(
                    {
                        type: 'submitNewTopic',
                        data: postData
                    }
                ).then(() => {
                    this.setContainerStatus(false);
                });

                this.resetComponent();
            },

            togglePollScheduler() {
                //toggle the scheduler
                this.poll_scheduler_opened = !this.poll_scheduler_opened;

                if (!this.poll_scheduled_date) {
                    this.prevent_from_closing = true;

                    this.$nextTick(() => {
                        //if its visible on the page
                        if (this.$refs.pollDatePicker) {
                            //activate it
                            this.$refs.pollDatePicker.$el.firstElementChild.click();
                        } else {
                            //if its not visible that means we are closing it
                            setTimeout(() => {
                                this.prevent_from_closing = false;
                            }, 100);
                        }
                    });
                }
            },

            setPollDate(date) {
                if (date) {
                    this.poll_scheduled_date = date;

                    setTimeout(() => {
                        this.prevent_from_closing = false;
                    }, 100);
                }
            },

            createPoll() {
                //prepare data for poll storing
                let poll_data = {
                    title: this.pollQuestion,
                    description: '',
                    type: 'poll',
                    settings: this.poll_settings,
                };

                if (this.poll_scheduled_date !== '') {
                    poll_data.settings['end_date'] = moment(this.poll_scheduled_date).format('YYYY-MM-DD HH:mm:ss');
                }

                //prepare data for question storing
                let question_data = {
                    is_active: true,
                    question: this.pollQuestion,
                    questionable_type: 'multiple_choice',
                    type: 'multiple_choice',
                    score: 1,
                    answer: '',
                    question_options: this.poll_answers,
                };

                //store the poll
                this.$store.dispatch('storePoll', poll_data).then((poll) => {
                    //create a new question for the store (polls only have one question)
                    this.$store.dispatch('storePollQuestion', {
                        question: question_data,
                        poll_id: poll.id
                    }).then((question) => {
                        //prepare the options for the question
                        for (let i = 0; i < question_data.question_options.length; i++) {
                            question_data.question_options[i].question_id = question.id;
                        }

                        question.question_options = question_data.question_options;

                        //store all the options
                        this.$store.dispatch('storePollQuestionOptions', question).then(() => {
                            //create the topic
                            this.createTopic(poll.id);
                        });
                    });
                });
            },

            updateNewPostTitle(payload) {
                this.$store.commit('updateNewPostTitle', payload.value);
            },

            updateNewPostContent(payload) {
                this.$store.commit('updateNewPostContent', payload.value);
            },

            updateNewPostVideoUrl(payload) {
                this.$store.commit('updateNewPostVideoUrl', payload.value);
            },

            updateNewPollQuestion(payload) {
                this.$store.commit('updateNewPollQuestion', payload.value);
            },

            openImageUploader() {
                if (this.postImagesCount < 4) {
                    this.$refs.newPostImagePicker.click();
                }
            },

            openPollImageUploader() {
                this.$refs.newPollImagePicker.click();
            },

            addImage(event) {
                if (event.target.files.length > 0 && (this.postType === 'poll' || (this.postType === 'image' && !this.postImages.includes(event.target.files[0])))) {
                    let file = event.target.files[0];
                    let self = this;

                    //generate the uuid that we will use to link the topic to this upload
                    let uuid = this.generateUUID();

                    //add it's uuid to the currently uploading list
                    this.currently_uploading_images.push(uuid);

                    //display the preview box so we immediately see a box even while image rendering is going on
                    this.show_upload_preview_box = true;

                    //wait for preview box to render
                    this.$nextTick(async () => {
                        if (this.postType === 'image') {
                            //we store the image so we can display it in the topic
                            this.$store.commit('addNewPostImage', {
                                file: event.target.files[0],
                                uuid: uuid,
                            });
                        } else if (this.postType === 'poll') {
                            this.$store.commit('addNewPollImage', {
                                file: event.target.files[0],
                                uuid: uuid,
                            });
                        }

                        if (["image/heic", "image/heif"].includes(file.type)) {
                            const pngFromHeic = await heic2any({ blob: file, toType: "image/png" });
                            file = new File([pngFromHeic], `${file.name.substring(0, file.name.lastIndexOf("."))}.png`, { type: "image/png" });
                        }

                        //prepare the image preview
                        loadImage.parseMetaData(file, function(data) {
                            //default image orientation
                            let orientation = 0;

                            //if exif data available, update orientation
                            if (data.exif) {
                                orientation = data.exif.get('Orientation');
                            }

                            loadImage(
                                file,
                                function(canvas) {
                                    //add the parsed image with correct orientation to the previews
                                    if (self.postType === 'image') {
                                        self.$store.commit('addNewPostImagePreview', canvas.toDataURL('image/jpeg'));
                                    } else if (self.postType === 'poll') {
                                        self.$store.commit('addNewPollImagePreview', canvas.toDataURL('image/jpeg'));
                                    }

                                    self.show_upload_preview_box = false;
                                }, {
                                    //should be set to canvas : true to activate auto fix orientation
                                    canvas: true,
                                    orientation: orientation
                                }
                            );
                        });
                    });
                }
            },

            updateAnswerValue(payload) {
                let active_answer = this.poll_answers.find(answer => answer.uid === payload.key);

                active_answer.value = payload.value;
            },

            addPollAnswer() {
                this.poll_answers.push({
                    uid: Date.now() + ((Math.random() * 100000).toFixed()),
                    value: '',
                })
            },

            deleteAnswer(answer, index) {
                this.poll_answers.splice(index, 1);
            },

            switchPollSetting(setting) {
                if (this.poll_settings[setting] === '1') {
                    this.poll_settings[setting] = '0';
                } else {
                    this.poll_settings[setting] = '1';
                }
            },

            removeImageFromUploadingList(uuid) {
                //remove this images' uuid from the currently uploading list
                this.currently_uploading_images.splice(this.currently_uploading_images.indexOf(uuid), 1);
            },

            removeImage(payload) {
                if(this.postType === 'image') {
                    this.$refs.newPostImagePicker.value = '';
                } else if (this.postType === 'poll') {
                    this.$refs.newPollImagePicker.value = '';
                }

                //delete from uploads
                if (!this.currently_uploading_images.includes(payload.uuid)) {
                    this.$store.dispatch('removeImage', payload.uuid).then(() => {
                        //remove the image from previews
                        if (this.postType === 'image') {
                            this.$store.commit('removeNewPostImage', payload.index);
                        } else if (this.postType === 'poll') {
                            this.$store.commit('removeNewPollImage', payload.index);
                        }
                    });
                } else if (this.currently_uploading_images.includes(payload.uuid)) {
                    if (this.postType === 'image') {
                        this.$store.commit('removeNewPostImage', payload.index);
                    } else if (this.postType === 'poll') {
                        this.$store.commit('removeNewPollImage', payload.index);
                    }

                    this.removeImageFromUploadingList(payload.uuid);
                }
            },

            onCreated() {
                //hide boards when in group feed or sub board
                if (boardId !== -1 || this.board_id_prop) {
                    this.boardsVisible = false;
                    this.$store.commit('setActiveBoard', [
                        {
                            id: boardId !== -1 ? boardId : this.board_id_prop,
                        },
                        'group']
                    );
                }
            },

            toggleImageUpload() {
                this.show_poll_image_upload = !this.show_poll_image_upload;
            }
        },

        created() {
            //create the smooth scroller
            this.$nextTick(() => {
                this.$smoothReflow({
                    el: this.$refs.newPostContainer,
                    hideOverflow: true
                });
            });
        },

        directives: {
            clickOutside: vClickOutside.directive
        },
    };
</script>

<style lang="scss">
    .new-post {
        .form-validation-error {
            @apply tw-mb-4;
        }

        .froala-wrapper {
            .fr-box {
                &.fr-basic {
                    margin-bottom: 8px;
                }
            }
        }

        .video-preview-wrapper {
            width: 250px;
            border-radius: 5px;
            overflow: hidden;
            margin-top: 10px;
            pointer-events: none;
            opacity: 0.5;
        }

        .card-top {
            @media(max-width: 767px) {
                h2 {
                    margin-left: 12px;
                    font-size: 14px;
                }
            }
        }

        .new-post-content {
            .new-post-title {
                .fr-box {
                    &.fr-basic {
                        margin-bottom: 0;
                        background: none;
                    }

                    &.active {
                        &.fr-basic {
                            background: #eaecef !important;
                        }
                    }
                }

                &:not(.prestine) {
                    .fr-box {
                        &.fr-basic {
                            background: #f0f0f1;
                        }
                    }
                }

                &.prestine {
                    margin-bottom: 0;
                }
            }

            .sequential-fields {
                .huddle-editor-wrapper {
                    margin-top: 10px;
                }

                .poll-settings {
                    .poll-scheduled-date {
                        position: relative;
                        margin-top: 12px;

                        &:before {
                            display: none;
                            position: absolute;
                            content: "\f073";
                            font-family: 'Font Awesome 5 Pro';
                            color: #c7c7c7;
                        }

                        input {
                            display: none;
                            background: none;
                            border: 0;
                            padding: 0;
                            padding-left: 20px;
                            margin-top: 10px;
                            color: #c7c7c7;
                            font-size: 13px;

                            &:hover {
                                cursor: pointer;
                            }
                        }

                        &:hover {
                            cursor: pointer;
                        }

                        &.show {
                            &:before {
                                display: block;
                            }

                            input {
                                display: block;
                            }
                        }
                    }

                    .huddle-editor {
                        margin-top: 0;
                    }
                }
            }

            .huddle-editor-wrapper {
                &.border-warning {
                    &.active-huddle-editor {
                        border: 1px solid #bb3825 !important;

                        .huddle-editor {
                            &::-webkit-input-placeholder {
                                color: #bb3825 !important;
                            }
                        }
                    }
                }
            }

            .huddle-mentions-wrapper {
                max-height: 100px;
                overflow: scroll;
            }
        }

        .scheduler-wrapper {
            bottom: calc(100% + 10px);

            input {
                display: none;
                font-size: 13px;
                box-shadow: 0 14px 28px rgba(0, 0, 0, 0.25), 0 10px 10px rgba(0, 0, 0, 0.22);
                color: #fff;
                font-weight: 600;
                border: 0;
                border-radius: 10px;
                background: #3c3c3c;

                &:hover {
                    cursor: pointer;
                }
            }

            &.show {
                input {
                    display: block;
                }
            }
        }

        .media-actions {
            margin-top: 15px;
            display: flex !important;

            .media-action {
                width: 30px;
                height: 30px;
                margin-right: 5px;
                border-radius: 5px;
                color: #a0a0a0;
                transition: background 0.2s ease-in-out;
                display: flex !important;

                i {
                    font-size: 18px;
                    transition: color 0.2s ease-in-out;
                }

                span {
                    top: -2px;
                    right: -2px;
                    font-size: 7px;
                    border-radius: 100%;
                    width: 12px;
                    height: 12px;
                    font-weight: 900;
                    box-shadow: -1px 1px 3px 0px #b1b1b1;
                    display: flex !important;
                }

                &:hover {
                    cursor: pointer;
                    background: #ececec;
                }

                &.active {
                    background: #ececec;
                }
            }
        }

        .new-board-select {
            border-radius: 3px;
            color: #fff;
            padding: 5px;
            padding-left: 7px;
            padding-right: 7px;
            font-weight: 500;
            margin-right: 5px;
            margin-bottom: 9px;
            display: inline-block;
            background: dimgrey;
            font-size: 12px;

            &.is-active {
                opacity: 0.8;
            }
        }

        input, textarea {
            &.is-invalid {
                border: 1px solid #bb3825 !important;
            }
        }

        .input-warning {
            margin-bottom: 10px;
            margin-top: 3px;
            display: block;
            color: #bb3825;
            font-size: 12px !important;
            font-style: italic;
            padding-bottom: 0 !important;
        }

        .image-previews {
            margin-top: 10px;

            .image-preview {
                display: inline-block;
                margin-right: 10px;
                width: 130px;
                height: 130px;
                padding: 10px;
                border-radius: 3px;
                background: rgba(0, 0, 0, 0.3);
                transition: border 0.3s ease-in-out;
                border: 2px solid transparent;

                img {
                    object-fit: cover;
                    height: 100%;
                    width: 100%;
                }

                &:hover {
                    cursor: pointer;
                    border: 2px solid #ff9999;
                }
            }
        }

        .filedrop-trigger-inner {
            display: inline-block;
            padding: 10px;
            height: 130px;
            width: 130px;
            text-align: center;
            font-size: 50px;
            vertical-align: top;

            &:after {
                content: "\f067";
                font-family: 'Font Awesome 5 Pro';
                display: inline-block;
                transition: transform 0.3s;
                margin-top: 17px;
            }

            &:hover {
                cursor: pointer;

                &:after {
                    transform: rotate(90deg);
                }
            }
        }

        &.open {
            .huddle-editor-wrapper {
                border: 1px solid #d6d6d6;
            }

            .new-post-title {
                .fr-box {
                    margin-bottom: 8px !important;

                    &.fr-basic {
                        background: #f0f0f1;
                    }

                    &.active {
                        &.fr-basic {
                            background: #eaecef;
                        }
                    }
                }
            }
        }
    }
</style>
